import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Button, Card, Form, Input, Message, Table, Icon, Label, Progress} from 'semantic-ui-react';
import {useNavigate, useParams} from 'react-router-dom';
import {API, copy, getChannelModels, showError, showInfo, showSuccess, verifyJSON,} from '../../helpers';
import {CHANNEL_OPTIONS} from '../../constants';
import {renderChannelTip} from '../../helpers/render';

const MODEL_MAPPING_EXAMPLE = {
  'gpt-3.5-turbo-0301': 'gpt-3.5-turbo',
  'gpt-4-0314': 'gpt-4',
  'gpt-4-32k-0314': 'gpt-4-32k',
};

function type2secretPrompt(type, t) {
  switch (type) {
    case 52:
      return '账号标识，如 default（对应 /opt/codex/accounts/default）';
    case 15:
      return t('channel.edit.key_prompts.zhipu');
    case 18:
      return t('channel.edit.key_prompts.spark');
    case 22:
      return t('channel.edit.key_prompts.fastgpt');
    case 23:
      return t('channel.edit.key_prompts.tencent');
    default:
      return t('channel.edit.key_prompts.default');
  }
}

const EditChannel = () => {
  const { t } = useTranslation();
  const params = useParams();
  const navigate = useNavigate();
  const channelId = params.id;
  const isEdit = channelId !== undefined;
  const [loading, setLoading] = useState(isEdit);
  const handleCancel = () => {
    navigate('/channel');
  };

  const originInputs = {
    name: '',
    type: 1,
    key: '',
    base_url: '',
    other: '',
    model_mapping: '',
    model_config: '',
    system_prompt: '',
    models: [],
    groups: ['default'],
    quality: 'green',
    priority: 2,
  };
  const QUALITY_OPTIONS = [
    { key: 'green', text: '绿色（G-）- 高稳定性', value: 'green' },
    { key: 'yellow', text: '黄色（Y-）- 中稳定性', value: 'yellow' },
    { key: 'red', text: '红色（R-）- 低稳定性', value: 'red' },
  ];
  const PRIORITY_OPTIONS = [
    { key: 3, text: '一级（最高优先）', value: 3 },
    { key: 2, text: '二级', value: 2 },
    { key: 1, text: '三级（最低优先）', value: 1 },
  ];
  const [batch, setBatch] = useState(false);
  const [inputs, setInputs] = useState(originInputs);
  const [originModelOptions, setOriginModelOptions] = useState([]);
  const [modelOptions, setModelOptions] = useState([]);
  const [groupOptions, setGroupOptions] = useState([]);
  const [basicModels, setBasicModels] = useState([]);
  const [fullModels, setFullModels] = useState([]);
  const [customModel, setCustomModel] = useState('');
  const [modelConfig, setModelConfig] = useState([]);
  const [fetchingModels, setFetchingModels] = useState(false);
  const [batchTesting, setBatchTesting] = useState(false);
  const [batchTestResults, setBatchTestResults] = useState([]);
  const [batchTestDone, setBatchTestDone] = useState(false);
  const abortControllerRef = useRef(null);
  const [config, setConfig] = useState({
    region: '',
    sk: '',
    ak: '',
    user_id: '',
    vertex_ai_project_id: '',
    vertex_ai_adc: '',
  });
  const handleInputChange = (e, { name, value }) => {
    setInputs((inputs) => ({ ...inputs, [name]: value }));
    if (name === 'type') {
      let localModels = getChannelModels(value);
      if (inputs.models.length === 0) {
        setInputs((inputs) => ({ ...inputs, models: localModels }));
      }
      setBasicModels(localModels);
    }
  };

  const handleConfigChange = (e, { name, value }) => {
    setConfig((inputs) => ({ ...inputs, [name]: value }));
  };

  const loadChannel = async () => {
    let res = await API.get(`/api/channel/${channelId}`);
    const { success, message, data } = res.data;
    if (success) {
      if (data.models === '') {
        data.models = [];
      } else {
        data.models = data.models.split(',');
      }
      if (data.group === '') {
        data.groups = [];
      } else {
        data.groups = data.group.split(',');
      }
      if (data.model_mapping !== '') {
        data.model_mapping = JSON.stringify(
          JSON.parse(data.model_mapping),
          null,
          2
        );
      }
      if (!data.quality) {
        data.quality = 'green';
      }
      if (!data.priority) {
        data.priority = 2;
      }
      // Parse model_config (3-tier naming) if available
      if (data.model_config && data.model_config !== '' && data.model_config !== '[]') {
        try {
          const configs = JSON.parse(data.model_config);
          setModelConfig(configs);
        } catch (e) {
          // If parse fails, fall back to models
          const models = data.models || [];
          setModelConfig(models.map((m) => ({ provider: m, mapping: m })));
        }
      } else if (data.models && data.models.length > 0) {
        // Backward compatibility: convert models to model config entries
        setModelConfig(data.models.map((m) => ({ provider: m, mapping: m })));
      }
      setInputs(data);
      if (data.config !== '') {
        setConfig(JSON.parse(data.config));
      }
      setBasicModels(getChannelModels(data.type));
    } else {
      showError(message);
    }
    setLoading(false);
  };

  const fetchModels = async () => {
    try {
      let res = await API.get(`/api/channel/models`);
      let localModelOptions = res.data.data.map((model) => ({
        key: model.id,
        text: model.id,
        value: model.id,
      }));
      setOriginModelOptions(localModelOptions);
      setFullModels(res.data.data.map((model) => model.id));
    } catch (error) {
      showError(error.message);
    }
  };

  const fetchGroups = async () => {
    try {
      let res = await API.get(`/api/group/`);
      setGroupOptions(
        res.data.data.map((group) => ({
          key: group,
          text: group,
          value: group,
        }))
      );
    } catch (error) {
      showError(error.message);
    }
  };

  const fetchFromProvider = async () => {
    if (!inputs.key || inputs.key === '') {
      showError('请先填写密钥');
      return;
    }
    setFetchingModels(true);
    try {
      let res = await API.post('/api/channel/fetch_models', {
        type: inputs.type,
        base_url: inputs.base_url,
        key: inputs.key,
      });
      const { success, message, data } = res.data;
      if (success) {
        const newConfig = data.map((name) => ({
          provider: name,
          mapping: name,
        }));
        setModelConfig(newConfig);
        showSuccess(`成功拉取 ${data.length} 个模型`);
      } else {
        showError(message);
      }
    } catch (error) {
      showError(error.message);
    }
    setFetchingModels(false);
  };

  const handleBatchTest = async () => {
    if (!inputs.key || inputs.key === '') {
      showError('请先填写密钥');
      return;
    }
    if (modelConfig.length === 0) {
      showError('请先拉取或添加模型');
      return;
    }
    setBatchTesting(true);
    setBatchTestResults([]);
    setBatchTestDone(false);

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    try {
      const response = await fetch('/api/channel/batch_test', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        signal: abortController.signal,
        body: JSON.stringify({
          type: inputs.type,
          base_url: inputs.base_url,
          key: inputs.key,
          config: JSON.stringify(config),
          other: inputs.other || '',
          models: modelConfig.map((m) => ({
            provider: m.provider,
            mapping: m.mapping,
          })),
        }),
      });

      if (!response.ok) {
        showError('请求失败：HTTP ' + response.status);
        setBatchTesting(false);
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n\n');
        buffer = lines.pop();

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              if (data.type === 'done') {
                showSuccess(
                  `测试完成：通过 ${data.passed} / 失败 ${data.failed} / 超时 ${data.timeout} / 未测试 ${data.pending}`
                );
              } else {
                setBatchTestResults((prev) => [...prev, data]);
              }
            } catch (e) {
              // ignore parse errors
            }
          }
        }
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        showInfo('轮询已停止');
      } else {
        showError(error.message);
      }
    }
    setBatchTesting(false);
    setBatchTestDone(true);
    abortControllerRef.current = null;
  };

  const handleStopTest = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  };

  const removeFailedModels = () => {
    const toRemove = new Set(
      batchTestResults
        .filter((r) => r.status === 'failed' || r.status === 'timeout')
        .map((r) => r.model)
    );
    const newConfig = modelConfig.filter((cfg) => {
      const name = cfg.provider || cfg.mapping;
      return !toRemove.has(name);
    });
    const removed = modelConfig.length - newConfig.length;
    setModelConfig(newConfig);
    setBatchTestResults([]);
    setBatchTestDone(false);
    showSuccess(`已移除 ${removed} 个不通过的模型，剩余 ${newConfig.length} 个`);
  };

  const addModelRow = () => {
    setModelConfig([...modelConfig, { provider: '', mapping: '' }]);
  };

  const removeModelRow = (index) => {
    setModelConfig(modelConfig.filter((_, i) => i !== index));
  };

  const updateModelRow = (index, field, value) => {
    const newConfig = [...modelConfig];
    newConfig[index] = { ...newConfig[index], [field]: value };
    setModelConfig(newConfig);
  };

  useEffect(() => {
    let localModelOptions = [...originModelOptions];
    inputs.models.forEach((model) => {
      if (!localModelOptions.find((option) => option.key === model)) {
        localModelOptions.push({
          key: model,
          text: model,
          value: model,
        });
      }
    });
    setModelOptions(localModelOptions);
  }, [originModelOptions, inputs.models]);

  useEffect(() => {
    if (isEdit) {
      loadChannel().then();
    } else {
      let localModels = getChannelModels(inputs.type);
      setBasicModels(localModels);
    }
    fetchModels().then();
    fetchGroups().then();
  }, []);

  const submit = async () => {
    if (inputs.key === '') {
      if (config.ak !== '' && config.sk !== '' && config.region !== '') {
        inputs.key = `${config.ak}|${config.sk}|${config.region}`;
      } else if (
        config.region !== '' &&
        config.vertex_ai_project_id !== '' &&
        config.vertex_ai_adc !== ''
      ) {
        inputs.key = `${config.region}|${config.vertex_ai_project_id}|${config.vertex_ai_adc}`;
      }
    }
    if (!isEdit && (inputs.name === '' || inputs.key === '')) {
      showInfo(t('channel.edit.messages.name_required'));
      return;
    }
    if (inputs.type !== 43 && modelConfig.length === 0 && inputs.models.length === 0) {
      showInfo('请至少添加一个模型');
      return;
    }
    if (inputs.model_mapping !== '' && !verifyJSON(inputs.model_mapping)) {
      showInfo(t('channel.edit.messages.model_mapping_invalid'));
      return;
    }
    let localInputs = { ...inputs };
    if (localInputs.key === 'undefined|undefined|undefined') {
      localInputs.key = ''; // prevent potential bug
    }
    if (localInputs.base_url && localInputs.base_url.endsWith('/')) {
      localInputs.base_url = localInputs.base_url.slice(
        0,
        localInputs.base_url.length - 1
      );
    }
    if (localInputs.type === 3 && localInputs.other === '') {
      localInputs.other = '2024-03-01-preview';
    }
    let res;
    // If modelConfig has entries, use it as the primary model configuration
    if (modelConfig.length > 0) {
      localInputs.model_config = JSON.stringify(modelConfig);
      // Also set models field with mapping names for backward compatibility
      localInputs.models = modelConfig
        .map((c) => c.mapping)
        .filter((m) => m)
        .join(',');
    } else {
      localInputs.model_config = '';
      localInputs.models = localInputs.models.join(',');
    }
    localInputs.group = localInputs.groups.join(',');
    localInputs.config = JSON.stringify(config);
    if (isEdit) {
      res = await API.put(`/api/channel/`, {
        ...localInputs,
        id: parseInt(channelId),
      });
    } else {
      res = await API.post(`/api/channel/`, localInputs);
    }
    const { success, message } = res.data;
    if (success) {
      if (isEdit) {
        showSuccess(t('channel.edit.messages.update_success'));
      } else {
        showSuccess(t('channel.edit.messages.create_success'));
        setInputs(originInputs);
      }
    } else {
      showError(message);
    }
  };

  const addCustomModel = () => {
    if (customModel.trim() === '') return;
    if (inputs.models.includes(customModel)) return;
    let localModels = [...inputs.models];
    localModels.push(customModel);
    let localModelOptions = [];
    localModelOptions.push({
      key: customModel,
      text: customModel,
      value: customModel,
    });
    setModelOptions((modelOptions) => {
      return [...modelOptions, ...localModelOptions];
    });
    setCustomModel('');
    handleInputChange(null, { name: 'models', value: localModels });
  };

  return (
    <div className='dashboard-container'>
      <Card fluid className='chart-card'>
        <Card.Content>
          <Card.Header className='header'>
            {isEdit
              ? t('channel.edit.title_edit')
              : t('channel.edit.title_create')}
          </Card.Header>
          <Form loading={loading} autoComplete='new-password'>
            <Form.Field>
              <Form.Select
                label={t('channel.edit.type')}
                name='type'
                required
                search
                options={CHANNEL_OPTIONS}
                value={inputs.type}
                onChange={handleInputChange}
              />
            </Form.Field>
            <Form.Field>
              <Form.Input
                label={t('channel.edit.name')}
                name='name'
                placeholder={t('channel.edit.name_placeholder')}
                onChange={handleInputChange}
                value={inputs.name}
                required
              />
            </Form.Field>
            <Form.Field>
              <Form.Dropdown
                label={t('channel.edit.group')}
                placeholder={t('channel.edit.group_placeholder')}
                name='groups'
                required
                fluid
                multiple
                selection
                allowAdditions
                additionLabel={t('channel.edit.group_addition')}
                onChange={handleInputChange}
                value={inputs.groups}
                autoComplete='new-password'
                options={groupOptions}
              />
            </Form.Field>
            <Form.Field>
              <Form.Select
                label='质量等级'
                name='quality'
                options={QUALITY_OPTIONS}
                value={inputs.quality}
                onChange={handleInputChange}
              />
            </Form.Field>
            <Form.Field>
              <Form.Select
                label='优先级'
                name='priority'
                options={PRIORITY_OPTIONS}
                value={inputs.priority}
                onChange={handleInputChange}
              />
            </Form.Field>
            {renderChannelTip(inputs.type)}

            {/* Azure OpenAI specific fields */}
            {inputs.type === 3 && (
              <>
                <Message>
                  注意，<strong>模型部署名称必须和模型名称保持一致</strong>
                  ，因为 One API 会把请求体中的 model
                  参数替换为你的部署名称（模型名称中的点会被剔除），
                  <a
                    target='_blank'
                    href='https://github.com/songquanpeng/one-api/issues/133?notification_referrer_id=NT_kwDOAmJSYrM2NjIwMzI3NDgyOjM5OTk4MDUw#issuecomment-1571602271'
                  >
                    图片演示
                  </a>
                  。
                </Message>
                <Form.Field>
                  <Form.Input
                    label='AZURE_OPENAI_ENDPOINT'
                    name='base_url'
                    placeholder='请输入 AZURE_OPENAI_ENDPOINT，例如：https://docs-test-001.openai.azure.com'
                    onChange={handleInputChange}
                    value={inputs.base_url}
                    autoComplete='new-password'
                  />
                </Form.Field>
                <Form.Field>
                  <Form.Input
                    label='默认 API 版本'
                    name='other'
                    placeholder='请输入默认 API 版本，例如：2024-03-01-preview，该配置可以被实际的请求查询参数所覆盖'
                    onChange={handleInputChange}
                    value={inputs.other}
                    autoComplete='new-password'
                  />
                </Form.Field>
              </>
            )}

            {/* Custom base URL field */}
            {inputs.type === 8 && (
              <Form.Field>
                <Form.Input
                    required
                    label={t('channel.edit.proxy_url')}
                    name='base_url'
                    placeholder={t('channel.edit.proxy_url_placeholder')}
                    onChange={handleInputChange}
                    value={inputs.base_url}
                    autoComplete='new-password'
                />
              </Form.Field>
            )}
            {inputs.type === 50 && (
                <Form.Field>
                  <Form.Input
                      required
                  label={t('channel.edit.base_url')}
                  name='base_url'
                  placeholder={t('channel.edit.base_url_placeholder')}
                  onChange={handleInputChange}
                  value={inputs.base_url}
                  autoComplete='new-password'
                />
              </Form.Field>
            )}

            {inputs.type === 18 && (
              <Form.Field>
                <Form.Input
                  label={t('channel.edit.spark_version')}
                  name='other'
                  placeholder={t('channel.edit.spark_version_placeholder')}
                  onChange={handleInputChange}
                  value={inputs.other}
                  autoComplete='new-password'
                />
              </Form.Field>
            )}
            {inputs.type === 21 && (
              <Form.Field>
                <Form.Input
                  label={t('channel.edit.knowledge_id')}
                  name='other'
                  placeholder={t('channel.edit.knowledge_id_placeholder')}
                  onChange={handleInputChange}
                  value={inputs.other}
                  autoComplete='new-password'
                />
              </Form.Field>
            )}
            {inputs.type === 17 && (
              <Form.Field>
                <Form.Input
                  label={t('channel.edit.plugin_param')}
                  name='other'
                  placeholder={t('channel.edit.plugin_param_placeholder')}
                  onChange={handleInputChange}
                  value={inputs.other}
                  autoComplete='new-password'
                />
              </Form.Field>
            )}
            {inputs.type === 34 && (
              <Message>{t('channel.edit.coze_notice')}</Message>
            )}
            {inputs.type === 40 && (
              <Message>
                {t('channel.edit.douban_notice')}
                <a
                  target='_blank'
                  href='https://console.volcengine.com/ark/region:ark+cn-beijing/endpoint'
                >
                  {t('channel.edit.douban_notice_link')}
                </a>
                {t('channel.edit.douban_notice_2')}
              </Message>
            )}
            {inputs.type !== 43 && inputs.type !== 52 && (
              <Form.Field>
                <Button
                  type={'button'}
                  loading={fetchingModels}
                  onClick={fetchFromProvider}
                  primary
                >
                  <Icon name='cloud download' />
                  拉取服务商模型列表
                </Button>
                <span style={{ marginLeft: '10px', color: '#888', fontSize: '13px' }}>
                  填写密钥和地址后点击拉取，自动获取服务商支持的模型
                </span>
              </Form.Field>
            )}
            {inputs.type !== 43 && (
              <Form.Field>
                <label>模型配置（三层名称映射）</label>
                <Table celled compact>
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell width={7}>服务商名称<br/><small style={{color:'#888'}}>Provider Name (发送给服务商)</small></Table.HeaderCell>
                      <Table.HeaderCell width={7}>映射名称<br/><small style={{color:'#888'}}>Mapping Name (对外暴露)</small></Table.HeaderCell>
                      <Table.HeaderCell width={1}>操作</Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {modelConfig.length === 0 && (
                      <Table.Row>
                        <Table.Cell colSpan={3} textAlign='center' style={{ color: '#888' }}>
                          暂无模型，请点击上方“拉取服务商模型列表”或手动添加
                        </Table.Cell>
                      </Table.Row>
                    )}
                    {modelConfig.map((cfg, index) => (
                      <Table.Row key={index}>
                        <Table.Cell>
                          <Input
                            fluid
                            value={cfg.provider}
                            onChange={(e, { value }) => updateModelRow(index, 'provider', value)}
                            placeholder='服务商返回的原始模型ID'
                          />
                        </Table.Cell>
                        <Table.Cell>
                          <Input
                            fluid
                            value={cfg.mapping}
                            onChange={(e, { value }) => updateModelRow(index, 'mapping', value)}
                            placeholder='对外暴露的名称（可加质量前缀）'
                          />
                        </Table.Cell>
                        <Table.Cell textAlign='center'>
                          <Button
                            type={'button'}
                            icon
                            color='red'
                            size='mini'
                            onClick={() => removeModelRow(index)}
                          >
                            <Icon name='trash' />
                          </Button>
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
                <div style={{ marginTop: '5px' }}>
                  <Button type={'button'} basic size='small' onClick={addModelRow}>
                    <Icon name='plus' />
                    添加模型
                  </Button>
                  <span style={{ marginLeft: '10px', color: '#888', fontSize: '13px' }}>
                    映射名称是用户请求时使用的名称，服务商名称是实际发送给上游的模型ID
                  </span>
                </div>

                {/* One-click batch test */}
                <div style={{ marginTop: '15px', padding: '10px', background: '#f7f7f7', borderRadius: '5px' }}>
                  <Button
                    type={'button'}
                    color='blue'
                    loading={batchTesting}
                    onClick={handleBatchTest}
                    disabled={modelConfig.length === 0 || batchTesting}
                  >
                    <Icon name='check circle' />
                    一键测试所有模型
                  </Button>
                  {batchTesting && (
                    <Button
                      type={'button'}
                      color='orange'
                      onClick={handleStopTest}
                      style={{ marginLeft: '10px' }}
                    >
                      <Icon name='stop circle' />
                      停止轮询
                    </Button>
                  )}
                  <span style={{ marginLeft: '10px', color: '#888', fontSize: '13px' }}>
                    并发测试，每模型超时30s，停止后未测的模型不会被删除
                  </span>

                  {batchTestResults.length > 0 && (
                    <>
                      <div style={{ marginTop: '10px' }}>
                        {(() => {
                          const total = modelConfig.length;
                          const tested = batchTestResults.length;
                          const pct = total > 0 ? Math.round((tested / total) * 100) : 0;
                          return (
                            <Progress
                              percent={pct}
                              size='small'
                              progress='ratio'
                              label={`${tested}/${total}`}
                            />
                          );
                        })()}
                      </div>
                      <Table celled compact size='small'>
                        <Table.Header>
                          <Table.Row>
                            <Table.HeaderCell width={4}>模型</Table.HeaderCell>
                            <Table.HeaderCell width={1}>状态</Table.HeaderCell>
                            <Table.HeaderCell width={2}>耗时</Table.HeaderCell>
                            <Table.HeaderCell width={9}>详情</Table.HeaderCell>
                          </Table.Row>
                        </Table.Header>
                        <Table.Body>
                          {batchTestResults.map((r, idx) => (
                            <Table.Row
                              key={idx}
                              positive={r.status === 'success'}
                              negative={r.status === 'failed' || r.status === 'timeout'}
                              warning={r.status === 'pending'}
                            >
                              <Table.Cell>
                                <Label basic size='small'>{r.model}</Label>
                              </Table.Cell>
                              <Table.Cell>
                                {r.status === 'success' && <Label color='green' size='mini'>通过</Label>}
                                {r.status === 'failed' && <Label color='red' size='mini'>失败</Label>}
                                {r.status === 'timeout' && <Label color='orange' size='mini'>超时</Label>}
                                {r.status === 'pending' && <Label color='grey' size='mini'>未测试</Label>}
                              </Table.Cell>
                              <Table.Cell>{r.time ? `${r.time.toFixed(2)}s` : '-'}</Table.Cell>
                              <Table.Cell style={{ fontSize: '12px', wordBreak: 'break-all', color: r.status === 'success' ? '#666' : '#c00' }}>
                                {r.message}
                              </Table.Cell>
                            </Table.Row>
                          ))}
                        </Table.Body>
                      </Table>
                      {batchTestDone && batchTestResults.some((r) => r.status === 'failed' || r.status === 'timeout') && (
                        <Button
                          type={'button'}
                          color='red'
                          size='small'
                          onClick={removeFailedModels}
                        >
                          <Icon name='trash' />
                          移除不通过的模型（失败+超时）
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </Form.Field>
            )}
            {inputs.type !== 43 && (
              <>
                <Form.Field>
                  <Form.TextArea
                    label={t('channel.edit.system_prompt')}
                    placeholder={t('channel.edit.system_prompt_placeholder')}
                    name='system_prompt'
                    onChange={handleInputChange}
                    value={inputs.system_prompt}
                    style={{
                      minHeight: 150,
                      fontFamily: 'JetBrains Mono, Consolas',
                    }}
                    autoComplete='new-password'
                  />
                </Form.Field>
              </>
            )}
            {inputs.type === 33 && (
              <Form.Field>
                <Form.Input
                  label='Region'
                  name='region'
                  required
                  placeholder={t('channel.edit.aws_region_placeholder')}
                  onChange={handleConfigChange}
                  value={config.region}
                  autoComplete=''
                />
                <Form.Input
                  label='AK'
                  name='ak'
                  required
                  placeholder={t('channel.edit.aws_ak_placeholder')}
                  onChange={handleConfigChange}
                  value={config.ak}
                  autoComplete=''
                />
                <Form.Input
                  label='SK'
                  name='sk'
                  required
                  placeholder={t('channel.edit.aws_sk_placeholder')}
                  onChange={handleConfigChange}
                  value={config.sk}
                  autoComplete=''
                />
              </Form.Field>
            )}
            {inputs.type === 42 && (
              <Form.Field>
                <Form.Input
                  label='Region'
                  name='region'
                  required
                  placeholder={t('channel.edit.vertex_region_placeholder')}
                  onChange={handleConfigChange}
                  value={config.region}
                  autoComplete=''
                />
                <Form.Input
                  label={t('channel.edit.vertex_project_id')}
                  name='vertex_ai_project_id'
                  required
                  placeholder={t('channel.edit.vertex_project_id_placeholder')}
                  onChange={handleConfigChange}
                  value={config.vertex_ai_project_id}
                  autoComplete=''
                />
                <Form.Input
                  label={t('channel.edit.vertex_credentials')}
                  name='vertex_ai_adc'
                  required
                  placeholder={t('channel.edit.vertex_credentials_placeholder')}
                  onChange={handleConfigChange}
                  value={config.vertex_ai_adc}
                  autoComplete=''
                />
              </Form.Field>
            )}
            {inputs.type === 34 && (
              <Form.Input
                label={t('channel.edit.user_id')}
                name='user_id'
                required
                placeholder={t('channel.edit.user_id_placeholder')}
                onChange={handleConfigChange}
                value={config.user_id}
                autoComplete=''
              />
            )}
            {inputs.type !== 33 &&
              inputs.type !== 42 &&
              (batch ? (
                <Form.Field>
                  <Form.TextArea
                    label={t('channel.edit.key')}
                    name='key'
                    required
                    placeholder={t('channel.edit.batch_placeholder')}
                    onChange={handleInputChange}
                    value={inputs.key}
                    style={{
                      minHeight: 150,
                      fontFamily: 'JetBrains Mono, Consolas',
                    }}
                    autoComplete='new-password'
                  />
                </Form.Field>
              ) : (
                <Form.Field>
                  <Form.Input
                    label={t('channel.edit.key')}
                    name='key'
                    required
                    placeholder={type2secretPrompt(inputs.type, t)}
                    onChange={handleInputChange}
                    value={inputs.key}
                    autoComplete='new-password'
                  />
                </Form.Field>
              ))}
            {inputs.type === 37 && (
              <Form.Field>
                <Form.Input
                  label='Account ID'
                  name='user_id'
                  required
                  placeholder={
                    '请输入 Account ID，例如：d8d7c61dbc334c32d3ced580e4bf42b4'
                  }
                  onChange={handleConfigChange}
                  value={config.user_id}
                  autoComplete=''
                />
              </Form.Field>
            )}
            {inputs.type !== 33 && !isEdit && (
              <Form.Checkbox
                checked={batch}
                label={t('channel.edit.batch')}
                name='batch'
                onChange={() => setBatch(!batch)}
              />
            )}
            {inputs.type !== 3 &&
              inputs.type !== 33 &&
              inputs.type !== 8 &&
                inputs.type !== 50 &&
              inputs.type !== 22 &&
              inputs.type !== 52 && (
                <Form.Field>
                  <Form.Input
                      label={t('channel.edit.proxy_url')}
                    name='base_url'
                      placeholder={t('channel.edit.proxy_url_placeholder')}
                    onChange={handleInputChange}
                    value={inputs.base_url}
                    autoComplete='new-password'
                  />
                </Form.Field>
              )}
            {inputs.type === 22 && (
              <Form.Field>
                <Form.Input
                  label='私有部署地址'
                  name='base_url'
                  placeholder={
                    '请输入私有部署地址，格式为：https://fastgpt.run/api/openapi'
                  }
                  onChange={handleInputChange}
                  value={inputs.base_url}
                  autoComplete='new-password'
                />
              </Form.Field>
            )}
            <Button onClick={handleCancel}>
              {t('channel.edit.buttons.cancel')}
            </Button>
            <Button
              type={isEdit ? 'button' : 'submit'}
              positive
              onClick={submit}
            >
              {t('channel.edit.buttons.submit')}
            </Button>
          </Form>
        </Card.Content>
      </Card>
    </div>
  );
};

export default EditChannel;
